import api from '@/services/api/client';

// Supplier type returned by GetCollectorSuppliers
export interface CollectorSupplier {
  id: string | number;
  name: string;
  shortName?: string;
  logoUrl?: string;
  portalName?: string;
  // Accept any additional fields without failing
  [key: string]: any;
}

export async function getCollectorSuppliers(search?: string): Promise<CollectorSupplier[]> {
  // Backend expects ?supplierName=... with min length handled on server
  const resp = await api.get('/v1/Supplier/GetCollectorSuppliers', {
    params: search && search.trim().length > 0 ? { supplierName: search } : undefined,
  });
  const data = resp?.data;
  // Common API response wrappers: { data: [...] } or direct [...]
  const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
  return list as CollectorSupplier[];
}

export interface TestPortalBotRequest {
  // Backend may accept either supplierId or newPortalName to identify the portal
  supplierId?: string | number;
  newPortalName?: string;
  username: string;
  password: string;
}

export interface TestPortalBotResponse {
  hasCredentialsCorrect?: boolean;
  accounts?: any[];
  filePaths?: string[];
  accountCount?: number;
  errorMessage?: string;
  // Preserve unknown fields from backend
  [key: string]: any;
}

export async function testPortalBot(payload: TestPortalBotRequest): Promise<TestPortalBotResponse> {
  const resp = await api.post('/v1/Collector/TestPortalBot', payload);
  const data = resp?.data;
  // Unwrap common envelope shapes
  return (data?.data ?? data) as TestPortalBotResponse;
}

// Download a test portal invoice file by posting filePath as form data
export async function getTestPortalInvoice(filePath: string): Promise<Blob> {
  const form = new FormData();
  form.append('filePath', filePath);
  const resp = await api.post('/v1/Collector/GetTestPortalInvoice', form, {
    responseType: 'blob',
  });
  return resp.data as Blob;
}

// Submit a portal integration request
export interface PortalIntegrationRequestPayload {
  supplierName: string; // from portalName input
  url: string; // from portalUrl input
  username: string; // test username
  password: string; // test password
  email: string; // contact email
  additionalInfo?: string; // optional description/details
  authUserId?: string | number | null; // attach from session if available
}

export interface PortalIntegrationRequestResponse {
  success?: boolean;
  message?: string;
  [key: string]: any;
}

export async function submitCollectorPortalRequest(
  payload: PortalIntegrationRequestPayload
): Promise<PortalIntegrationRequestResponse> {
  const resp = await api.post('/v1/supplier/collector-portal-request', payload);
  const data = resp?.data;
  return (data?.data ?? data) as PortalIntegrationRequestResponse;
}
